import { openRoute, purchaseAircraft } from './commands';
import { applyDifficultyMilestone } from './difficulty';
import type { GameState, WeeklyReport } from './types';

export type ChallengeStartId = 'cashflow-squeeze' | 'fleet-overhang' | 'credit-expansion';

export interface ChallengeStartDefinition {
  id: ChallengeStartId;
  title: string;
  detail: string;
  aircraft: string[];
  bankLoan: number;
  cash: number;
  revenue: number;
  operatingCosts: number;
  interest: number;
}

export const CHALLENGE_STARTS: readonly ChallengeStartDefinition[] = [
  { id: 'cashflow-squeeze', title: '现金流紧绷', detail: '机队不大但票价偏低，先找出最值得提价和维护的航线。', aircraft: ['ATR72', 'ATR72', 'ATR72'], bankLoan: 5_000_000, cash: 7_000_000, revenue: 1_180_000, operatingCosts: 1_420_000, interest: 88_000 },
  { id: 'fleet-overhang', title: '运力过剩', detail: '几架飞机和多条航线已经投入运营，调整运力配置才能止住亏损。', aircraft: ['ATR72', 'ATR72', 'ARJ21', 'ARJ21'], bankLoan: 8_000_000, cash: 10_000_000, revenue: 2_480_000, operatingCosts: 3_080_000, interest: 150_000 },
  { id: 'credit-expansion', title: '授信扩张', detail: '授信空间较大但负债成本也高，报表会提示你从偿债和票价中寻找突破口。', aircraft: ['ARJ21', 'ARJ21', 'ARJ21'], bankLoan: 12_000_000, cash: 13_000_000, revenue: 2_100_000, operatingCosts: 2_520_000, interest: 220_000 },
];

const CHALLENGE_DESTINATIONS = ['SZX', 'CAN', 'SHA', 'PVG'] as const;

function reportFor(week: number, definition: ChallengeStartDefinition): WeeklyReport {
  const netProfit = definition.revenue - definition.operatingCosts - definition.interest;
  return {
    week,
    revenue: definition.revenue,
    operatingCosts: definition.operatingCosts,
    operatingCostBreakdown: { fuel: definition.operatingCosts * 0.34, landing: definition.operatingCosts * 0.16, passenger: definition.operatingCosts * 0.2, admin: definition.operatingCosts * 0.12, lease: 0, assistantSalary: definition.operatingCosts * 0.18 },
    hubCosts: 120_000,
    operatingProfit: definition.revenue - definition.operatingCosts,
    depreciation: 80_000,
    interest: definition.interest,
    principalPaid: 55_000,
    netProfit,
    netCashFlow: netProfit - 55_000,
    uniquePassengers: Math.round(definition.revenue / 220),
    segmentPassengers: Math.round(definition.revenue / 190),
    seats: 1_800,
    averageLoadFactor: 0.68,
    unmetDemand: 420,
    fuelPriceIndex: 1.08,
    fuelGallons: 28_000,
    signals: [{ id: `challenge-loss-${week}`, tone: 'negative', title: '经营利润为负', detail: '请结合航线报表、票价、机况和贷款利息寻找扭亏方案。' }],
  };
}

export function applyChallengeStart(state: GameState, startId: ChallengeStartId): GameState {
  const definition = CHALLENGE_STARTS.find((item) => item.id === startId) ?? CHALLENGE_STARTS[0];
  let next = applyDifficultyMilestone({ ...state, week: 3 });
  next.week = state.week;
  for (const typeId of definition.aircraft) next = purchaseAircraft(next, typeId, 'new', 'finance');
  for (const [index, aircraft] of next.aircraft.entries()) {
    const destination = CHALLENGE_DESTINATIONS[index];
    if (destination) next = openRoute(next, next.headquartersId, destination, aircraft.id);
  }
  const bankLoanId = `challenge-bank-${next.nextId++}`;
  next.loans.push({ id: bankLoanId, kind: 'bank', originalPrincipal: definition.bankLoan, principalRemaining: definition.bankLoan, annualRate: 0.085, weeklyPayment: Math.round(definition.bankLoan * 0.085 / 52), weeksRemaining: 260 });
  next.ledger.push({ id: `ledger-${next.nextId++}`, week: next.week, category: 'debt_draw', cashAmount: definition.bankLoan, profitAmount: 0, objectId: bankLoanId, label: '挑战开局授信提款' });
  next.week = 12;
  next.cash = definition.cash;
  next.reports = [8, 9, 10, 11].map((week) => reportFor(week, definition));
  next.lastReport = next.reports.at(-1);
  next.updatedAt = Date.now();
  return next;
}
